// Auto-generated by splitter.py — do not regenerate; edit directly.

import React, { useState, useMemo, useEffect } from 'react';
import { Search } from 'lucide-react';
import { COMMAND_PALETTE_ENTRIES } from '../constants/commandpalette';

const GROUP_ORDER = ['page', 'action', 'help'];
const GROUP_LABELS = { page: 'Go to', action: 'Create', help: 'Help' };

export const CommandPalette = ({ open, onClose, onNavigate, onAction }) => {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  // Adjust state during render (not in an effect) when `open` changes, so the
  // palette never flashes the previous session's query for a frame on reopen —
  // see react.dev "Adjusting some state when a prop changes".
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) { setQuery(''); setCursor(0); }
  }

  const filtered = useMemo(() => {
    const available = COMMAND_PALETTE_ENTRIES;
    if (!query.trim()) return available;
    const q = query.trim().toLowerCase();
    return available.filter(e => e.label.toLowerCase().includes(q) || (e.sub || '').toLowerCase().includes(q));
  }, [query]);

  useEffect(() => { if (cursor >= filtered.length) setCursor(0); }, [filtered.length, cursor]);

  // Keyboard nav inside palette
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setCursor(c => Math.min(c + 1, filtered.length - 1)); }
      else if (e.key === 'ArrowUp')   { e.preventDefault(); setCursor(c => Math.max(c - 1, 0)); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        const item = filtered[cursor];
        if (!item) return;
        if (item.kind === 'page')      onNavigate(item.id, item.search);
        else if (item.kind === 'action' || item.kind === 'help') onAction(item.action);
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, filtered, cursor, onClose, onNavigate, onAction]);

  if (!open) return null;
  const groups = filtered.reduce((acc, it) => { (acc[it.kind] = acc[it.kind] || []).push(it); return acc; }, {});

  // Map flat index per item for cursor highlighting
  let flatIdx = -1;

  return (
    <div className="fixed inset-0 z-[100] bg-[#1F2937]/40 flex items-start justify-center pt-[12vh] px-4" onClick={onClose}>
      <div className="bg-white border border-[#E6E4E1] rounded-[12px] w-full max-w-[600px] shadow-2xl overflow-hidden flex flex-col max-h-[70vh]" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[#EEEDEA]">
          <Search className="w-4 h-4 text-[#9CA3AF]" strokeWidth={2} />
          <input
            autoFocus
            value={query}
            onChange={(e) => { setQuery(e.target.value); setCursor(0); }}
            placeholder="Search"
            className="flex-1 bg-transparent text-[13.5px] text-[#1F2937] placeholder:text-[#9CA3AF] focus:outline-none"
          />
          <kbd className="text-[10px] text-[#9CA3AF] bg-[#F7F6F2] border border-[#E6E4E1] rounded px-1.5 py-0.5 font-mono">ESC</kbd>
        </div>

        <div className="flex-1 overflow-y-auto py-1">
          {filtered.length === 0 && (
            <div className="px-4 py-10 text-center text-[12.5px] text-[#9CA3AF]">No results for "{query}"</div>
          )}
          {GROUP_ORDER.map(g => {
            if (!groups[g]) return null;
            return (
              <div key={g} className="py-1">
                <div className="px-4 pt-2 pb-1 text-[10px] font-semibold tracking-[0.06em] uppercase text-[#9CA3AF]">{GROUP_LABELS[g]}</div>
                {groups[g].map((it) => {
                  flatIdx++;
                  const active = flatIdx === cursor;
                  const Icon = it.icon;
                  return (
                    <button
                      key={it.label}
                      onClick={() => { if (it.kind === 'page') onNavigate(it.id, it.search); else onAction(it.action); onClose(); }}
                      onMouseEnter={() => setCursor(flatIdx)}
                      className={`w-full text-left px-4 py-2 flex items-center gap-3 ${active ? 'bg-[#F2F1ED]' : 'hover:bg-[#FBFAF7]'}`}
                    >
                      <div className={`w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 ${active ? 'bg-white border border-[#E6E4E1]' : 'bg-[#FBFAF7] border border-[#EEEDEA]'}`}>
                        {Icon && <Icon className="w-3.5 h-3.5 text-[#4B5563]" strokeWidth={1.75} />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-medium text-[#1F2937] leading-tight">{it.label}</div>
                        {it.sub && <div className="text-[10.5px] text-[#6B7280] mt-0.5">{it.sub}</div>}
                      </div>
                      {it.keys && (
                        <div className="flex items-center gap-0.5 flex-shrink-0">
                          {it.keys.split(' ').map((k, i) => (
                            <kbd key={i} className="text-[10px] text-[#6B7280] bg-[#FBFAF7] border border-[#E6E4E1] rounded px-1.5 py-0.5 font-mono uppercase">{k}</kbd>
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t border-[#EEEDEA] bg-[#FBFAF7] text-[10.5px] text-[#6B7280] flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span><kbd className="bg-white border border-[#E6E4E1] rounded px-1 font-mono">↑↓</kbd> navigate</span>
            <span><kbd className="bg-white border border-[#E6E4E1] rounded px-1 font-mono">↵</kbd> select</span>
            <span><kbd className="bg-white border border-[#E6E4E1] rounded px-1 font-mono">esc</kbd> close</span>
          </div>
          <span>{filtered.length} of {COMMAND_PALETTE_ENTRIES.length}</span>
        </div>
      </div>
    </div>
  );
};
